// Type contracts for the soil-test analyzer pipeline.
//
// Pipeline:  PDF text -> extract-llm -> SoilTestExtraction -> normalize ->
//            SoilTestValues -> interpret -> SoilTestInterpretation
//
// Each stage's output is the next stage's input. Persistence row
// (`soil_test_results`) stores both `raw_values: SoilTestExtraction` and
// `interpretation: SoilTestInterpretation` plus `analysis_context` snapshot.

// The product-link type lives in the shared curated-picks engine; imported here
// for local use by RecommendedAction and re-exported below for back-compat.
import type { ProductRecommendationLink } from '@/lib/product-picks/types'

/** Canonical grass keys, matching `data/guide-content.ts` GRASS_GUIDES keys. */
export type GrassKey =
  | 'kentucky-bluegrass'
  | 'tall-fescue'
  | 'fine-fescue'
  | 'perennial-ryegrass'
  | 'bermudagrass'
  | 'zoysiagrass'
  | 'st-augustinegrass'
  | 'centipede'
  | 'bahiagrass'
  | 'carpetgrass'
  | 'buffalograss'
  | 'kikuyugrass'
  | 'annual-ryegrass'
  | 'mixed-cool-season-blend'

export const GRASS_KEYS: readonly GrassKey[] = [
  'kentucky-bluegrass',
  'tall-fescue',
  'fine-fescue',
  'perennial-ryegrass',
  'bermudagrass',
  'zoysiagrass',
  'st-augustinegrass',
  'centipede',
  'bahiagrass',
  'carpetgrass',
  'buffalograss',
  'kikuyugrass',
  'annual-ryegrass',
  'mixed-cool-season-blend',
] as const

export type LabName =
  | 'logan_labs'
  | 'waypoint'
  | 'spectrum'
  | 'al_eastern'
  | 'umass'
  | 'other'
  | 'manual'

export type ParsingConfidence = 'high' | 'medium' | 'low' | 'manual'

export type ValueConfidence = 'high' | 'medium' | 'low'

export type Season = 'spring' | 'summer' | 'fall' | 'winter'

export type ActionPriority = 'high' | 'medium' | 'low' | 'informational'

// ---------------------------------------------------------------------------
// Stage 1: extraction output (LLM-produced, raw from the report)
// ---------------------------------------------------------------------------

/**
 * One extracted value with the model's own evidence and self-assessed
 * confidence. `value: null` means the model could not find the value in the
 * report; `missingReason` carries the why.
 */
export interface SoilValueWithEvidence {
  value: number | null
  unit: string | null
  sourceText: string | null
  confidence: ValueConfidence
  missingReason: string | null
}

export interface BaseSaturationExtraction {
  ca_pct: SoilValueWithEvidence
  mg_pct: SoilValueWithEvidence
  k_pct: SoilValueWithEvidence
  h_pct: SoilValueWithEvidence
  na_pct: SoilValueWithEvidence
}

export interface SoilTestExtraction {
  lab_detected: LabName
  test_date: string | null
  ph: SoilValueWithEvidence
  organic_matter_pct: SoilValueWithEvidence
  cec: SoilValueWithEvidence
  phosphorus: SoilValueWithEvidence
  potassium: SoilValueWithEvidence
  calcium: SoilValueWithEvidence
  magnesium: SoilValueWithEvidence
  sulfur: SoilValueWithEvidence
  iron: SoilValueWithEvidence
  manganese: SoilValueWithEvidence
  zinc: SoilValueWithEvidence
  boron: SoilValueWithEvidence
  copper: SoilValueWithEvidence
  base_saturation: BaseSaturationExtraction | null
  parsing_confidence: ParsingConfidence
  parser_notes: string | null
}

// ---------------------------------------------------------------------------
// Stage 2: normalized values (canonical units, range-rejected values nulled)
// ---------------------------------------------------------------------------

/**
 * Indicates why a normalized value is null and/or how the normalizer altered
 * the extracted value. Surfaced to the user so low-confidence parses don't
 * masquerade as authoritative.
 */
export interface NormalizationWarning {
  field: string
  reason: 'out_of_range' | 'unit_unrecognized' | 'unit_conversion_failed' | 'missing' | 'low_confidence'
  detail: string
  originalValue: number | null
  originalUnit: string | null
}

export interface BaseSaturationValues {
  ca_pct: number | null
  mg_pct: number | null
  k_pct: number | null
  h_pct: number | null
  na_pct: number | null
}

/**
 * Normalized soil values in canonical units:
 * - pH: unitless (3-10 plausible range)
 * - organic_matter_pct: percent (0-15 plausible range)
 * - cec: meq/100g (1-50 plausible range)
 * - all nutrients in ppm
 * - base_saturation values in percent
 *
 * Any value the normalizer rejected (out of range, untranslatable unit) is
 * null; the reason is in `warnings`.
 */
export interface SoilTestValues {
  lab: LabName
  testDate: string | null
  parsingConfidence: ParsingConfidence
  ph: number | null
  organicMatterPct: number | null
  cec: number | null
  phosphorusPpm: number | null
  potassiumPpm: number | null
  calciumPpm: number | null
  magnesiumPpm: number | null
  sulfurPpm: number | null
  ironPpm: number | null
  manganesePpm: number | null
  zincPpm: number | null
  boronPpm: number | null
  copperPpm: number | null
  baseSaturation: BaseSaturationValues | null
  warnings: NormalizationWarning[]
}

// ---------------------------------------------------------------------------
// Stage 3: interpretation output (engine-produced, user-facing)
// ---------------------------------------------------------------------------

/**
 * Snapshot of inputs that fed into the interpretation. Saved alongside the
 * result so re-interpretation later (under new engine rules) can be done
 * deterministically without losing the original context.
 */
export interface AnalysisContext {
  grassType: GrassKey
  zipCode: string | null
  lawnSize: number | null // square feet
  region: string | null
  season: Season | null
}

export interface Citation {
  label: string
  url: string
  publisher: string
}

// Re-homed to lib/product-picks/types.ts so every calculator (not just soil-test)
// can share the curated-picks engine. Re-exported for back-compat with the
// soil-test modules that import it from `./types`.
export type { ProductRecommendationLink }

/**
 * One actionable (or informational) finding in the interpretation. Cards in
 * the results UI render one of these each.
 */
export interface RecommendedAction {
  /** Stable id so React keys + analytics events line up across re-renders. */
  id: string

  /** Which nutrient or condition this action addresses. */
  category:
    | 'ph'
    | 'potassium'
    | 'phosphorus'
    | 'organic_matter'
    | 'cec_context'
    | 'calcium_magnesium'
    | 'micronutrient'
    | 'sulfur'
    | 'sodium'
    | 'general'

  priority: ActionPriority

  /** One-line headline shown in the card header. */
  title: string

  /** Plain-English explanation of what we found and why it matters. */
  explanation: string

  /** Concrete next step, if any. Null for informational-only entries. */
  recommendation: string | null

  /** Lawn-size-adjusted amount (e.g. "33 lb pelletized lime"). */
  amount: string | null

  /** When in the year to do this. */
  timing: string | null

  /** Affiliate product picks for this action; may be empty. */
  products: ProductRecommendationLink[]

  /** University extension references backing the recommendation. */
  citations: Citation[]

  /** Any disclaimers specific to this action (e.g. cap reached, retest first). */
  caveats: string[]
}

export interface SoilTestInterpretation {
  /** Top-line summary suitable for an at-a-glance card. */
  summary: string

  /**
   * Whether the lab interpretation is operating in a degraded mode because of
   * low parse confidence, many missing values, or otherwise. UI may show a
   * banner.
   */
  degraded: boolean
  degradedReason: string | null

  /**
   * Actions sorted by priority. Phase 1 includes pH, K, P (overseeding only),
   * organic matter, CEC context, cautious Ca/Mg. Micronutrients + sulfur +
   * sodium are emitted as `informational` priority only.
   */
  actions: RecommendedAction[]

  /** Always present even on degraded parses so the user sees we processed it. */
  generatedAtIso: string
}
